import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { useMediaQuery, useTheme } from '@material-ui/core';
import ReactSelect from 'react-select';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import { format } from 'date-fns';
import pt from 'date-fns/locale/pt-BR';
import 'react-datepicker/dist/react-datepicker.css';

import { useAuth } from 'hooks/auth';
import { useHttp } from 'hooks/http';

import { FilterIcon } from '../../styles/icons';

import Pagination from '../../components/Pagination';
import { ButtonIcon } from '../../components/Forms';
import { Table } from '../../components/Table';
import { IPaginate } from '../../interfaces';

import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

import {
  Container,
  FiltersRow,
  TableSection,
  ChartPanel,
  ChartTitle,
  SummaryPanel,
  ChartsColumn,
  PieSummaryRow,
  BarChartPanel,
  ContentGrid,
  LeftColumnPane,
  RightColumnPane,
  TableScrollBox,
  ProximityDetailTableWrap,
} from './styles';
import Laydash from 'components/Layout/Laydash';

registerLocale('pt-BR', pt);

interface IServiceUserOption {
  value: string;
  label: string;
}

interface IManagementOption {
  value: number;
  label: string;
}

interface IAggregatedByUserRow {
  user_id: string;
  first_name: string;
  last_name: string;
  document_secondary: string | null;
  total: number;
}

interface IAssistanceRow {
  name: string;
  count: number;
}

interface IProximitySummary {
  total: number;
  by_assistance: IAssistanceRow[];
}

interface IDetailRow {
  agent_first_name: string | null;
  agent_last_name: string | null;
  assistance_type: string;
  distric: string | null;
  description: string | null;
}

const REFRESH_MS = 60_000;

/** Quantidade fixa de linhas na tabela de detalhes (proximidades). */
const DETAIL_TABLE_ROWS = 3;

const BAR_PALETTE = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088fe', '#00c49f', '#a28fd0', '#f06292'];

/* eslint-disable @typescript-eslint/no-explicit-any */
const analyticsSelectStyles = {
  control: (base: any) => ({
    ...base,
    minHeight: 40,
    border: '1px solid #ccc',
    borderRadius: 4,
    backgroundColor: '#fff',
    boxShadow: 'none',
  }),
  menu: (base: any) => ({
    ...base,
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    borderRadius: 4,
    zIndex: 20,
  }),
  menuList: (base: any) => ({
    ...base,
    backgroundColor: '#fff',
    padding: 0,
  }),
  option: (base: any, state: { isFocused: boolean; isSelected: boolean }) => ({
    ...base,
    backgroundColor: state.isSelected ? '#e8e8e8' : state.isFocused ? '#f5f5f5' : '#fff',
    color: '#333',
    cursor: 'pointer',
  }),
  singleValue: (base: any) => ({
    ...base,
    color: '#333',
  }),
  placeholder: (base: any) => ({
    ...base,
    color: '#666',
  }),
  input: (base: any) => ({
    ...base,
    color: '#333',
  }),
  indicatorSeparator: (base: any) => ({
    ...base,
    backgroundColor: '#ccc',
  }),
  dropdownIndicator: (base: any) => ({
    ...base,
    color: '#666',
  }),
};

const ProximityDash: React.FC = () => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const { user } = useAuth();
  const { httpGet } = useHttp();

  const [managements, setManagements] = useState<IManagementOption[]>([]);
  const [selectedManagement, setSelectedManagement] = useState<IManagementOption | null>(null);

  const [serviceUserOptions, setServiceUserOptions] = useState<IServiceUserOption[]>([]);
  const [serviceUser, setServiceUser] = useState<IServiceUserOption | null>(null);
  const [draftStart, setDraftStart] = useState<Date>(() => new Date());
  const [draftEnd, setDraftEnd] = useState<Date>(() => new Date());
  const [appliedStart, setAppliedStart] = useState<Date>(() => new Date());
  const [appliedEnd, setAppliedEnd] = useState<Date>(() => new Date());
  const [aggregatedRows, setAggregatedRows] = useState<IAggregatedByUserRow[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [paginate, setPaginate] = useState<IPaginate>({
    page: 1, lastPage: 1, perPage: 10,
  });
  const [proximitySummary, setProximitySummary] = useState<IProximitySummary | null>(null);
  const [detailRows, setDetailRows] = useState<IDetailRow[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const managementId = selectedManagement?.value ?? user?.allocation?.management_id;

  const loadServiceUsers = useCallback(async () => {
    if (!managementId) return;
    const { status, data } = await httpGet('proximities/analytics/service-users', {
      params: {
        management_id: managementId,
        startDate: format(appliedStart, 'yyyy-MM-dd'),
        endDate: format(appliedEnd, 'yyyy-MM-dd'),
      },
    }, false);
    if (status === 200 && Array.isArray(data)) {
      const opts = data.map((u: {
        id: string;
        first_name: string;
        last_name: string;
        document_secondary: string | null;
      }) => ({
        value: u.id,
        label: [u.document_secondary, u.first_name, u.last_name].filter(Boolean).join(' — '),
      }));
      setServiceUserOptions(opts);
      setServiceUser((prev) => {
        if (!prev) return null;
        return opts.some((o) => o.value === prev.value) ? prev : null;
      });
    }
  }, [httpGet, managementId, appliedStart, appliedEnd]);

  useEffect(() => {
    loadServiceUsers();
  }, [loadServiceUsers]);

  const loadManagements = useCallback(async () => {
    const { status, data } = await httpGet('/sectors/managements');
    if (status === 200 && Array.isArray(data)) {
      const managementParsed: IManagementOption[] = data.map((management: { id: number; initials: string }) => ({
        value: management.id,
        label: management.initials,
      }));
      setManagements(managementParsed);
    }
  }, [httpGet]);

  useEffect(() => {
    loadManagements();
  }, [loadManagements]);

  useEffect(() => {
    const allocId = user?.allocation?.management_id;
    if (!allocId || managements.length === 0) return;
    setSelectedManagement((prev) => {
      if (prev) return prev;
      return managements.find((m) => m.value === allocId) ?? null;
    });
  }, [managements, user]);

  const fetchList = useCallback(async (opts?: { silent?: boolean }) => {
    if (!managementId) return;
    if (!opts?.silent) {
      setListLoading(true);
    }
    try {
      const params: Record<string, string | number> = {
        management_id: managementId,
        startDate: format(appliedStart, 'yyyy-MM-dd'),
        endDate: format(appliedEnd, 'yyyy-MM-dd'),
        page,
        perPage: 10,
      };
      if (serviceUser?.value) {
        params.service_user_id = serviceUser.value;
      }

      const res = await httpGet('proximities/analytics/grouped-by-user', { params }, false);

      if (
        res
        && typeof res === 'object'
        && 'status' in res
        && (res as { status: number }).status === 200
        && 'data' in res
        && (res as { data: { data?: IAggregatedByUserRow[]; pagination?: IPaginate } }).data
      ) {
        const body = (res as { data: { data: IAggregatedByUserRow[]; pagination: IPaginate } }).data;
        setAggregatedRows(body.data || []);
        setPaginate(body.pagination);
      } else {
        setAggregatedRows([]);
      }
    } finally {
      if (!opts?.silent) {
        setListLoading(false);
      }
    }
  }, [managementId, appliedStart, appliedEnd, page, serviceUser, httpGet]);

  const fetchSummary = useCallback(async (opts?: { silent?: boolean }) => {
    if (!managementId) return;
    try {
      const params: Record<string, string | number> = {
        management_id: managementId,
        startDate: format(appliedStart, 'yyyy-MM-dd'),
        endDate: format(appliedEnd, 'yyyy-MM-dd'),
      };
      if (serviceUser?.value) {
        params.service_user_id = serviceUser.value;
      }

      const res = await httpGet('proximities/analytics/summary', { params }, false);

      if (
        res
        && typeof res === 'object'
        && 'status' in res
        && (res as { status: number }).status === 200
        && 'data' in res
      ) {
        const data = (res as { data: IProximitySummary }).data;
        setProximitySummary(data);
      } else {
        setProximitySummary({ total: 0, by_assistance: [] });
      }
    } catch {
      setProximitySummary({ total: 0, by_assistance: [] });
    }
  }, [managementId, appliedStart, appliedEnd, serviceUser, httpGet]);

  const fetchDetailRows = useCallback(async (opts?: { silent?: boolean }) => {
    if (!managementId) return;
    if (!opts?.silent) {
      setDetailLoading(true);
    }
    try {
      const params: Record<string, string | number> = {
        management_id: managementId,
        startDate: format(appliedStart, 'yyyy-MM-dd'),
        endDate: format(appliedEnd, 'yyyy-MM-dd'),
        page: 1,
        perPage: DETAIL_TABLE_ROWS,
      };
      if (serviceUser?.value) {
        params.service_user_id = serviceUser.value;
      }

      const res = await httpGet('proximities/analytics/detail-rows', { params }, false);

      if (
        res
        && typeof res === 'object'
        && 'status' in res
        && (res as { status: number }).status === 200
        && 'data' in res
        && (res as { data: { data?: IDetailRow[]; pagination?: IPaginate } }).data
      ) {
        const body = (res as { data: { data: IDetailRow[]; pagination?: IPaginate } }).data;
        setDetailRows(body.data || []);
      } else {
        setDetailRows([]);
      }
    } finally {
      if (!opts?.silent) {
        setDetailLoading(false);
      }
    }
  }, [managementId, appliedStart, appliedEnd, serviceUser, httpGet]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchDetailRows();
  }, [fetchDetailRows]);

  useEffect(() => {
    if (!managementId) return undefined;
    const timer = setInterval(() => {
      fetchList({ silent: true });
      fetchSummary({ silent: true });
      fetchDetailRows({ silent: true });
    }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [managementId, fetchList, fetchSummary, fetchDetailRows]);

  const handleManagementChange = (opt: IManagementOption | null) => {
    setSelectedManagement(opt);
    setPage(1);
    setServiceUser(null);
  };

  const handleServiceUserChange = (opt: IServiceUserOption | null) => {
    setPage(1);
    setServiceUser(opt);
  };

  const handleFilter = () => {
    setAppliedStart(draftStart);
    setAppliedEnd(draftEnd);
    setPage(1);
  };

  const handlePaginate = (p: IPaginate) => {
    setPage(p.page);
  };

  const perPage = paginate.perPage || 10;

  const detailColumns = useMemo(
    () => [
      {
        Header: '#',
        id: 'row_index',
        Cell: ({ row }: { row: { index: number } }) => row.index + 1,
      },
      {
        Header: 'Agente',
        id: 'agente',
        Cell: ({ row }: { row: { original: IDetailRow } }) => {
          const o = row.original;
          return [o.agent_first_name, o.agent_last_name].filter(Boolean).join(' ').trim() || '—';
        },
      },
      {
        Header: 'Assistência',
        accessor: 'assistance_type',
      },
      {
        Header: 'Distrito',
        id: 'distric',
        Cell: ({ row }: { row: { original: IDetailRow } }) => row.original.distric?.trim() || '—',
      },
      {
        Header: 'Descrição',
        id: 'description',
        Cell: ({ row }: { row: { original: IDetailRow } }) => {
          const full = row.original.description?.trim() || '';
          const text = full || '—';
          return (
            <div
              title={full || undefined}
              style={{
                display: 'block',
                maxWidth: 280,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: 'left',
              }}
            >
              {text}
            </div>
          );
        },
      },
    ],
    [],
  );

  const columns = useMemo(
    () => [
      {
        Header: '#',
        id: 'row_index',
        Cell: ({ row }: { row: { index: number } }) => (page - 1) * perPage + row.index + 1,
      },
      {
        Header: 'Usuário',
        id: 'nome',
        Cell: ({ row }: { row: { original: IAggregatedByUserRow } }) => {
          const o = row.original;
          return [o.first_name, o.last_name].filter(Boolean).join(' ').trim() || '—';
        },
      },
      {
        Header: 'Total',
        accessor: 'total',
      },
    ],
    [page, perPage],
  );

  const sortedAssistances = useMemo(() => {
    if (!proximitySummary?.by_assistance?.length) return [];
    return [...proximitySummary.by_assistance].sort((a, b) => b.count - a.count);
  }, [proximitySummary]);

  const topTwoAssistances = useMemo(
    () => sortedAssistances.slice(0, 2),
    [sortedAssistances],
  );

  const pieData = useMemo(() => {
    if (!proximitySummary || proximitySummary.total === 0 || sortedAssistances.length === 0) {
      return [];
    }
    const top2 = sortedAssistances.slice(0, 2);
    const restSum = sortedAssistances.slice(2).reduce((s, x) => s + x.count, 0);
    const rows = top2.map((x) => ({ name: x.name, value: x.count }));
    if (restSum > 0) {
      rows.push({ name: 'Demais', value: restSum });
    }
    return rows;
  }, [proximitySummary, sortedAssistances]);

  const assistanceBarHeight = useMemo(() => {
    const n = pieData.length;
    if (n === 0) return 120;
    const cap = isMdUp ? 280 : 220;
    return Math.min(cap, 56 + n * (isMdUp ? 40 : 34));
  }, [pieData.length, isMdUp]);

  const links = [
    { path: '/analytics', title: 'ANÁLISE', key: 1 },
    { path: '/proximity-dash', title: 'PROXIMIDADE', key: 2 },
    { path: '/dashboard', title: 'SAIR', key: 3 },
  ];

  return (
    <Laydash links={links}>
      {user?.allocation && (
        <Container>
          <FiltersRow>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={6} md={2} style={{ minWidth: 0 }}>
                <Typography variant="caption" display="block" gutterBottom>
                  Gerência
                </Typography>
                <ReactSelect<IManagementOption>
                  options={managements}
                  value={selectedManagement}
                  onChange={(opt) => handleManagementChange(opt as IManagementOption | null)}
                  placeholder="Selecione a gerência"
                  styles={analyticsSelectStyles}
                  noOptionsMessage={() => 'Nenhuma gerência'}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} style={{ minWidth: 0 }}>
                <Typography variant="caption" display="block" gutterBottom>
                  Policial escalado no período (opcional)
                </Typography>
                <ReactSelect
                  options={serviceUserOptions}
                  value={serviceUser}
                  onChange={(opt) => handleServiceUserChange(opt as IServiceUserOption | null)}
                  placeholder="Todos escalados no período"
                  styles={analyticsSelectStyles}
                  isClearable
                  noOptionsMessage={() => 'Nenhum policial escalado neste período'}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2} style={{ minWidth: 0 }}>
                <Typography variant="caption" display="block" gutterBottom>
                  Data inicial
                </Typography>
                <ReactDatePicker
                  selected={draftStart}
                  onChange={(d) => d && setDraftStart(d)}
                  dateFormat="dd/MM/yyyy"
                  locale="pt-BR"
                  maxDate={draftEnd}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2} style={{ minWidth: 0 }}>
                <Typography variant="caption" display="block" gutterBottom>
                  Data final
                </Typography>
                <ReactDatePicker
                  selected={draftEnd}
                  onChange={(d) => d && setDraftEnd(d)}
                  dateFormat="dd/MM/yyyy"
                  locale="pt-BR"
                  minDate={draftStart}
                  maxDate={new Date()}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} style={{ minWidth: 0 }}>
                <ButtonIcon type="button" title="Filtrar" onClick={handleFilter} isLoading={listLoading || detailLoading}>
                  <FilterIcon title="Filtrar" />
                </ButtonIcon>
              </Grid>
            </Grid>
          </FiltersRow>

          {managementId && (
            <ContentGrid>
              <LeftColumnPane>
                {aggregatedRows.length === 0 && !listLoading && (
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Nenhuma abordagem de proximidade agregada no período selecionado.
                  </Typography>
                )}
                {aggregatedRows.length > 0 && (
                  <TableSection>
                    <TableScrollBox>
                      <Table columns={columns} data={aggregatedRows} hidePagination />
                    </TableScrollBox>
                    <Pagination paginate={paginate} handlePaginate={handlePaginate} />
                  </TableSection>
                )}
              </LeftColumnPane>
              <RightColumnPane>
                <ChartsColumn>
                  <BarChartPanel>
                    <ChartTitle>Proximidades (agente, assistência, distrito e descrição)</ChartTitle>
                    {detailLoading && detailRows.length === 0 ? (
                      <Typography variant="body2" style={{ color: '#aaa' }}>
                        Carregando…
                      </Typography>
                    ) : detailRows.length === 0 ? (
                      <Typography variant="body2" style={{ color: '#aaa' }}>
                        Sem registros no período para exibir.
                      </Typography>
                    ) : (
                      <ProximityDetailTableWrap>
                        <TableSection>
                          <TableScrollBox>
                            <Table columns={detailColumns} data={detailRows} hidePagination />
                          </TableScrollBox>
                        </TableSection>
                      </ProximityDetailTableWrap>
                    )}
                  </BarChartPanel>

                  <PieSummaryRow>
                    <ChartPanel>
                      <ChartTitle>Distribuição por assistência (período filtrado)</ChartTitle>
                      {pieData.length === 0 ? (
                        <Typography variant="body2" style={{ color: '#aaa' }}>
                          Sem dados no período para o gráfico.
                        </Typography>
                      ) : (
                        <ResponsiveContainer width="100%" height={assistanceBarHeight}>
                          <BarChart
                            layout="vertical"
                            data={pieData}
                            margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                            <XAxis type="number" stroke="#aaa" allowDecimals={false} />
                            <YAxis
                              type="category"
                              dataKey="name"
                              width={isMdUp ? 168 : 130}
                              tick={{ fill: '#ddd', fontSize: 11 }}
                              interval={0}
                              tickFormatter={(v: string) => (v.length > 32 ? `${v.slice(0, 29)}…` : v)}
                            />
                            <Tooltip
                              formatter={(value: number) => [`${value} abordagens`, 'Quantidade']}
                              labelFormatter={(label: string) => label}
                            />
                            <Bar dataKey="value" name="Quantidade" radius={[0, 4, 4, 0]}>
                              {pieData.map((entry, i) => (
                                <Cell key={`${entry.name}-${i}`} fill={BAR_PALETTE[i % BAR_PALETTE.length]} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </ChartPanel>

                    <SummaryPanel>
                      <ChartTitle>Resumo por assistência</ChartTitle>
                      <table>
                        <thead>
                          <tr>
                            <th>Assistência</th>
                            <th>Quantidade</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topTwoAssistances.map((row) => (
                            <tr key={row.name}>
                              <td>{row.name}</td>
                              <td>{row.count}</td>
                            </tr>
                          ))}
                          <tr>
                            <td>Total geral</td>
                            <td>{proximitySummary?.total ?? 0}</td>
                          </tr>
                        </tbody>
                      </table>
                    </SummaryPanel>
                  </PieSummaryRow>
                </ChartsColumn>
              </RightColumnPane>
            </ContentGrid>
          )}
        </Container>
      )}
    </Laydash>
  );
};

export default ProximityDash;
